import React, { useEffect, useMemo, useState } from "react";
import ReportTablePage from "./ReportTablePage";
import {
  getTransactionReport,
  getReportLookups,
} from "../../services/api";

export default function TransactionReportPage() {
  const [rows, setRows] = useState([]);
  const [lookups, setLookups] = useState({
    kanda: [],
    jumuia: [],
    types: [],
  });
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
    status: "",
    channel: "",
    contributionType: "",
    memberSearch: "",
    kandaId: "",
    jumuiaId: "",
  });

  useEffect(() => {
    loadLookups();
    loadReport();
  }, []);

  const loadLookups = async () => {
    try {
      const res = await getReportLookups();
      setLookups(res.data || {});
    } catch (err) {
      console.error(err);
    }
  };

  const loadReport = async () => {
    try {
      setLoading(true);
      const res = await getTransactionReport(filters);
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      dateFrom: "",
      dateTo: "",
      status: "",
      channel: "",
      contributionType: "",
      memberSearch: "",
      kandaId: "",
      jumuiaId: "",
    });
  };

  const filteredJumuia = useMemo(() => {
    if (!filters.kandaId) return lookups.jumuia || [];
    return (lookups.jumuia || []).filter(
      (j) => String(j.kanda_id) === String(filters.kandaId)
    );
  }, [lookups.jumuia, filters.kandaId]);

  const formatMoney = (value) =>
    Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const columns = [
  {
    label: "Reference",
    key: "reference_no",
    render: (row) => row.reference_no || `TRX-${row.transaction_id}`,
    exportValue: (row) =>
      row.reference_no || `TRX-${row.transaction_id}`,
  },

  // MEMBER DETAILS (split)
  {
    label: "Name",
    key: "full_name",
    render: (row) => row.full_name || "-",
    exportValue: (row) => row.full_name || "",
  },
  {
    label: "MemberCode",
    key: "member_code",
    render: (row) => row.member_code || "-",
    exportValue: (row) => row.member_code || "",
  },
  {
    label: "Phone",
    key: "phone",
    render: (row) => row.phone || "-",
    exportValue: (row) => row.phone || "",
  },

  // CONTRIBUTION (split)
  {
    label: "Contribution",
    key: "contribution_type",
    render: (row) => row.contribution_type || "-",
    exportValue: (row) => row.contribution_type || "",
  },
  {
    label: "Amount",
    key: "amount",
    render: (row) => `TSH ${formatMoney(row.amount)}`,
    exportValue: (row) => formatMoney(row.amount),
  },
  {
    label: "Channel",
    key: "channel",
    render: (row) => row.channel || "-",
    exportValue: (row) => row.channel || "",
  },

  // HIERARCHY (split)
  {
    label: "Jimbo",
    key: "jimbo_name",
    render: (row) => row.jimbo_name || "-",
    exportValue: (row) => row.jimbo_name || "",
  },
  {
    label: "Parokia",
    key: "parokia_name",
    render: (row) => row.parokia_name || "-",
    exportValue: (row) => row.parokia_name || "",
  },
  {
    label: "Kanda",
    key: "kanda_name",
    render: (row) => row.kanda_name || "-",
    exportValue: (row) => row.kanda_name || "",
  },
  {
    label: "Jumuiya",
    key: "jumuia_name",
    render: (row) => row.jumuia_name || "-",
    exportValue: (row) => row.jumuia_name || "",
  },

  // POSTING DETAILS (split)
  {
    label: "Status",
    key: "status",
    render: (row) => row.status || "-",
    exportValue: (row) => row.status || "",
  },
  {
    label: "Cashier",
    key: "cashier_name",
    render: (row) => row.cashier_name || "-",
    exportValue: (row) => row.cashier_name || "",
  },
  {
    label: "Date",
    key: "transaction_date",
    render: (row) =>
      row.transaction_date
        ? new Date(row.transaction_date).toLocaleString()
        : "-",
    exportValue: (row) => row.transaction_date || "",
  },
];

  const filterNodes = (
    <>
      <div>
        <label className="field-label">Date From</label>
        <input
          type="date"
          className="input"
          value={filters.dateFrom}
          onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
        />
      </div>

      <div>
        <label className="field-label">Date To</label>
        <input
          type="date"
          className="input"
          value={filters.dateTo}
          onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
        />
      </div>

      <div>
        <label className="field-label">Status</label>
        <select
          className="input"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="">All</option>
          <option value="POSTED">POSTED</option>
          <option value="REVERSED">REVERSED</option>
        </select>
      </div>

      <div>
        <label className="field-label">Channel</label>
        <select
          className="input"
          value={filters.channel}
          onChange={(e) => setFilters({ ...filters, channel: e.target.value })}
        >
          <option value="">All</option>
          <option value="CASH">CASH</option>
          <option value="BANK">BANK</option>
          <option value="MOBILE">MOBILE</option>
        </select>
      </div>

      <div>
        <label className="field-label">Contribution Type</label>
        <select
          className="input"
          value={filters.contributionType}
          onChange={(e) =>
            setFilters({ ...filters, contributionType: e.target.value })
          }
        >
          <option value="">All</option>
          {(lookups.types || []).map((t) => (
            <option key={t.type_id} value={t.name}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="field-label">Kanda</label>
        <select
          className="input"
          value={filters.kandaId}
          onChange={(e) =>
            setFilters({
              ...filters,
              kandaId: e.target.value,
              jumuiaId: "",
            })
          }
        >
          <option value="">All</option>
          {(lookups.kanda || []).map((k) => (
            <option key={k.kanda_id} value={k.kanda_id}>
              {k.kanda_name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="field-label">Jumuiya</label>
        <select
          className="input"
          value={filters.jumuiaId}
          onChange={(e) => setFilters({ ...filters, jumuiaId: e.target.value })}
        >
          <option value="">All</option>
          {filteredJumuia.map((j) => (
            <option key={j.jumuia_id} value={j.jumuia_id}>
              {j.jumuia_name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="field-label">Search</label>
        <input
          className="input"
          placeholder="Member, code, phone, reference..."
          value={filters.memberSearch}
          onChange={(e) =>
            setFilters({ ...filters, memberSearch: e.target.value })
          }
        />
      </div>
    </>
  );

  return (
    <ReportTablePage
      title="Transaction Report"
    //   subtitle="Detailed collections report for your Parokia"
      filters={filterNodes}
      rows={rows}
      columns={columns}
      loading={loading}
      onSearch={loadReport}
      onClear={clearFilters}
      exportFileName="transaction_report.csv"
    />
  );
}